<script setup lang="ts">
//#region import
import { computed } from 'vue';
import { get } from 'lodash-es';
import { useTypedStore } from '@/store';
import type { GetControlTowerFeesBySiteIdResponse, PutControlTowerFeesBySiteIdData } from '@/types/openapi-ts';
import type { PutControlTowerFeesBySiteIdErrors } from './EditFeesPopup.vue';
//#endregion

//#region types
export type Fee = GetControlTowerFeesBySiteIdResponse['payload']['admin'] & {
  field?: string
};

export interface Props {
  fee?: Fee,
  errors?: PutControlTowerFeesBySiteIdErrors[keyof PutControlTowerFeesBySiteIdData['body']],
}

export interface Emits {
  addCountry: [event: PointerEvent, fee: Fee],
  removeCountry: [i: number, item: Fee],
}
//#endregion

//#region define
const props = withDefaults(defineProps<Props>(), {
  fee: () => ({
    default_fee_percent: 0,
    custom_fees: [],
  }),
  errors: () => ({}),
});

const emit = defineEmits<Emits>();
//#endregion

//#region composables
const store = useTypedStore();
//#endregion

//#region computed
const countries = computed(() => store.getters['misc/countries']);
//#endregion

//#region methods
const filterCountries = (filter: Fee['custom_fees'], curCode: string) => {
  return countries.value.filter((lang) => !filter.some((el) => el.country_code === lang.code && lang.code !== curCode));
};

const addCountry = (event: PointerEvent, fee: Fee) => {
  emit('addCountry', event, fee);
};
const removeCountry = (i: number, item: Fee) => {
  emit('removeCountry', i, item);
};

const getError = (field: string, index = 0) => {
  const fieldPath = Array.isArray(field) ? field : field.split('.');
  return get(props.errors, [...fieldPath, index, 'message'], null);
};
//#endregion
</script>

<template>
  <div class="fees-block t:space-y-16">
    <div class="fees-block__header">
      <span class="fees-block__header-title">
        {{ $t(`settings.fees.${fee!.field}`) }}
      </span>
      <UiButton
        icon="plus"
        no-border
        @click="addCountry($event, fee!)"
      >
        {{ $t('settings.fees.add_country') }}
      </UiButton>
    </div>
    <div class="fees-block__content">
      <div class="fees-block__row">
        <div class="t:flex t:justify-between t:items-center">
          <div class="fees-block__row-title">
            {{ $t('settings.fees.default') }}
          </div>
          <div class="t:flex t:ml-16">
            <UiInput
              v-model="fee!.default_fee_percent"
              error-tooltip
              post-text="%"
              width="82px"
              size="big"
              :is-error="!!getError('default_fee_percent')"
            />
            <UiIcon
              color="var(--text-negative)"
              class="t:ml-8"
              name="times"
              size="14px"
              disabled
              cursor
            />
          </div>
        </div>
        <UiHelp
          v-if="getError('default_fee_percent')"
          :text="getError('default_fee_percent')"
          is-error
        />
      </div>
      <div
        v-for="(feeRow, i) in fee!.custom_fees"
        :key="feeRow.country_code"
        class="fees-block__row"
      >
        <div class="t:flex t:justify-between">
          <UiSelectElement
            v-model="feeRow.country_code"
            :list="filterCountries(fee!.custom_fees, feeRow.country_code)"
            :placeholder="$t('settings.fees.placeholder_country')"
            filterable
            is-virtual
            option-value-param="code"
            size="big"
            :is-error="!!getError(`custom_fees.${i}.country_code`)"
          />
          <div class="t:flex t:items-center t:ml-16">
            <UiInput
              v-model.number="feeRow.fee_percent"
              post-text="%"
              width="82px"
              size="big"
              :is-error="!!getError(`custom_fees.${i}.fee_percent`)"
            />
            <UiIcon
              color="var(--text-negative)"
              class="t:ml-8"
              name="times"
              size="14px"
              cursor
              @click="removeCountry(i, fee!)"
            />
          </div>
        </div>
        <UiHelp
          v-if="getError(`custom_fees.${i}.fee_percent`) || getError(`custom_fees.${i}.country_code`)"
          :text="getError(`custom_fees.${i}.fee_percent`) || getError(`custom_fees.${i}.country_code`)"
          is-error
        />
      </div>
    </div>
  </div>
</template>

<style lang='scss'>
.fees-block{
  padding: 16px;
  border-radius: 8px;
  background: var(--background-surface);
  box-sizing: border-box;
  position: relative;

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  &__header-title {
    font-size: 14px;
    font-weight: 500;
  }
  &__row {
    margin-bottom: 8px;
    scroll-margin-bottom: 50px;
    display: flex;
    justify-content: space-between;
    flex-direction: column;
  }
  &__row-title {
    font-size: 12px;
  }
}
</style>
